import { Type } from "@sinclair/typebox"
import { useMemo } from "react"
import { useStoredState } from "../hooks/useStoredState"
import { type AppLocale, defaultLocale } from "./messages"

const LOCALE_STORAGE_KEY = "craft-force.locale"

const localeSchema = Type.Union([
  Type.Literal("en-US"),
  Type.Literal("de-DE"),
  Type.Literal("fr-FR"),
  Type.Literal("zh-Hans"),
  Type.Literal("zh-Hant"),
])

export function normalizeLocale(value: string): AppLocale | null {
  const normalized = value.trim().toLowerCase()

  if (normalized === "en" || normalized === "en-us") return "en-US"
  if (normalized === "de" || normalized === "de-de") return "de-DE"
  if (normalized === "fr" || normalized === "fr-fr") return "fr-FR"
  if (
    normalized === "zh-hans" ||
    normalized === "zh-cn" ||
    normalized === "zh-sg" ||
    normalized === "zh"
  ) {
    return "zh-Hans"
  }
  if (
    normalized === "zh-hant" ||
    normalized === "zh-tw" ||
    normalized === "zh-hk"
  ) {
    return "zh-Hant"
  }

  return null
}

export function resolveLocale(
  candidates: readonly (string | null | undefined)[],
): AppLocale {
  for (const candidate of candidates) {
    if (!candidate) continue
    const resolved = normalizeLocale(candidate)
    if (resolved) return resolved
  }

  return defaultLocale
}

function detectLocale(): AppLocale {
  if (typeof navigator === "undefined") return defaultLocale

  return resolveLocale([...(navigator.languages ?? []), navigator.language])
}

export function useLocale() {
  const initialLocale = useMemo(() => detectLocale(), [])
  const [locale, setLocale] = useStoredState<AppLocale>(
    LOCALE_STORAGE_KEY,
    localeSchema,
    {
      defaultValue: initialLocale,
      migrate: () => initialLocale,
      version: 1,
    },
  )

  return { locale, setLocale }
}
